import { useState, useEffect, useRef } from 'react';

/**
 * Timing budget (deliberately staggered):
 *   CSS animation:  1.2 s  — visual flash (realtimeAnimations.ts)
 *   Flash state:    1.5 s  — hook keeps flash non-null (allows CSS to finish + buffer)
 *   Cooldown:       2.0 s  — ignore subsequent changes (prevents rapid successive flashes)
 */
const COOLDOWN_MS = 2000;

/**
 * Detects numeric value changes and returns a flash direction.
 *
 * - Returns `'up'` when value increases, `'down'` when it decreases
 * - Resets to `null` after `duration` ms
 * - Skips the first render (no flash on initial load)
 * - When `trigger` is provided, only flashes when both the value AND
 *   the trigger reference changed. This prevents flashing on user actions
 *   (date range changes, account switches) — only RTDB live updates trigger a flash.
 * - After a flash, subsequent changes are ignored for COOLDOWN_MS.
 *
 * NOTE: `duration` is assumed stable for the lifetime of the component.
 * The reset timer is NOT tied to effect cleanup (see comment inside) so
 * a changed `duration` would only apply to the next flash, not the current one.
 */
export function useValueFlash(
    value: number,
    trigger?: unknown,
    duration = 1500
): 'up' | 'down' | null {
    const [flash, setFlash] = useState<'up' | 'down' | null>(null);
    const prevRef = useRef<{ value: number; trigger: unknown } | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastFlashAtRef = useRef(0);

    useEffect(() => {
        // Skip initial render — absorb first value silently
        if (prevRef.current === null) {
            prevRef.current = { value, trigger };
            return;
        }

        const prevValue = prevRef.current.value;
        const valueChanged = value !== prevValue;
        const triggerChanged = trigger !== prevRef.current.trigger;

        // Always update tracked values
        prevRef.current = { value, trigger };

        // If trigger is provided, only flash when trigger changed
        if (trigger !== undefined && !triggerChanged) return;

        // No value change → no flash
        if (!valueChanged) return;

        // Cooldown — ignore rapid successive flashes
        if (Date.now() - lastFlashAtRef.current < COOLDOWN_MS) return;

        const direction = value > prevValue ? 'up' : 'down';

        lastFlashAtRef.current = Date.now();

        // Clear any pending reset timer before scheduling a new one.
        // NOTE: We intentionally do NOT return a cleanup function here.
        // If we did, React would cancel the timer on the next dep change
        // (even if the new effect returns early), leaving flash stuck non-null.
        if (timerRef.current) clearTimeout(timerRef.current);

        setFlash(direction);
        timerRef.current = setTimeout(() => {
            setFlash(null);
            timerRef.current = null;
        }, duration);
    }, [value, trigger, duration]);

    // Clean up timer only on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    return flash;
}
